/**
 * Member Portal Web — Mixpanel client
 *
 * PHILOSOPHY (read this before adding events; full detail in src/analytics/README.md)
 * ---------------------------------------------------------------------------------
 *
 * Purpose: per-member WORKFLOW TELEMETRY — "what job did this member attempt, did it
 * succeed, what blocked them?" — not aggregate product/UX analytics (drop-off rates,
 * time-on-page, A/B comparisons).
 *
 * Keep the OVERLAP: events that are both behavioral and workflow-meaningful stay
 * (dashboard CTAs as workflow entry points, results-review decisions, survey blockers).
 * Drop events that only describe UI churn with no member-state value (test add/remove,
 * incidental modal dismissals).
 *
 * Event names: snake_case, stable, finite vocabulary — see analytics/events.ts.
 *   - Format: {domain}_{entity}_{outcome}  e.g. kit_order_batch_completed
 *   - NEVER put dynamic values in event names (no UUIDs, HTTP codes, form IDs).
 *   - Put variation in PROPERTIES: kit_uuid, survey_id, form_id, http_status, etc.
 *
 * Every event should carry:
 *   workflow       — member job (kit_order, results_review, auth, …) — primary filter
 *   workflow_step  — where in the job (cta_clicked, started, submitted, completed, …)
 *   outcome        — when applicable: success | failure | partial | blocked | deferred
 *
 * Super property (auto on every event):
 *   event_source: "member_portal_web"  — distinguishes web from future backend events
 *   in the same Mixpanel project. Backend should use event_source: "member_portal_api".
 *
 * People profile (mixpanel.people.set): workflow CAPABILITY state only — program, active
 * status, invite/gift eligibility, spouse benefit slot, cancer-diagnosis routing flag.
 * NEVER names, email, phone, DOB, address, or other PII. distinct_id is glMemberId via
 * identify().
 *
 * Where to add instrumentation:
 *   - Typed helpers in analytics/track.ts (or new functions there), not raw mixpanel.track.
 *   - Import from "analytics" in feature code.
 *
 * Environments:
 *   Events fire ONLY when REACT_APP_MIXPANEL_TIER is "test" or "prod" AND a token is set.
 *   Local dev (.env) uses the test tier token. Dev and UAT deploy env files omit Mixpanel vars.
 *
 * Cypress:
 *   On the test tier, events are suppressed during E2E (flag from cypress/support/e2e.js,
 *   plus window.Cypress / parent.Cypress). Needed because window.Cypress is often undefined
 *   after Auth0 cy.origin redirects. Manual QA on test is unaffected.
 *
 * Lexicon:
 *   Event/property definitions live in member_portal_lexicon.csv. When changing events,
 *   update that file and upload to Mixpanel Lexicon (test + prod). See README.
 *
 * @see src/analytics/README.md
 * @see src/analytics/member_portal_lexicon.csv
 */

import mixpanel from "mixpanel-browser";
import { ANALYTICS_EVENTS, AnalyticsEventName } from "./events";

const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const MIXPANEL_TIER = process.env.REACT_APP_MIXPANEL_TIER;

let mixpanelInitialized = false;

/** Set by cypress/support/e2e.js on window:before:load (survives Auth0 redirects). */
export const MEMBER_PORTAL_CYPRESS_E2E_FLAG = "__MEMBER_PORTAL_CYPRESS_E2E__";

type WindowWithCypress = Window & { Cypress?: unknown };

function readCypressFromWindow(win: WindowWithCypress): unknown {
  if (win.Cypress) {
    return win.Cypress;
  }
  try {
    const parent = win.parent as WindowWithCypress;
    if (parent && parent !== win && parent.Cypress) {
      return parent.Cypress;
    }
  } catch {
    // parent may be cross-origin
  }
  try {
    const top = win.top as WindowWithCypress;
    if (top && top !== win && top.Cypress) {
      return top.Cypress;
    }
  } catch {
    // top may be cross-origin
  }
  return undefined;
}

function isCypressTestRun(): boolean {
  const win = globalThis as unknown as WindowWithCypress & {
    [MEMBER_PORTAL_CYPRESS_E2E_FLAG]?: boolean;
  };

  if (win[MEMBER_PORTAL_CYPRESS_E2E_FLAG]) {
    return true;
  }

  return Boolean(readCypressFromWindow(win));
}

/** Whether this browser session should send Mixpanel data right now. */
export function shouldSendMixpanelEvents(): boolean {
  if (!MIXPANEL_TOKEN) {
    return false;
  }
  if (MIXPANEL_TIER !== "test" && MIXPANEL_TIER !== "prod") {
    return false;
  }
  if (MIXPANEL_TIER === "test" && isCypressTestRun()) {
    return false;
  }
  return true;
}

function initMixpanelIfNeeded(): void {
  if (mixpanelInitialized || !shouldSendMixpanelEvents() || !MIXPANEL_TOKEN) {
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, { persistence: "localStorage" });
  mixpanel.register({
    event_source: "member_portal_web"
  });
  mixpanelInitialized = true;
}

export function isMixpanelEnabled(): boolean {
  return shouldSendMixpanelEvents();
}

export function identifyMember(glMemberId: string): void {
  if (!glMemberId || !shouldSendMixpanelEvents()) {
    return;
  }
  initMixpanelIfNeeded();
  mixpanel.identify(glMemberId);
}

export function setMixpanelPeopleProperties(
  properties: Record<string, boolean | string | number>
): void {
  if (!shouldSendMixpanelEvents()) {
    return;
  }
  initMixpanelIfNeeded();
  mixpanel.people.set(properties);
}

export function serializeAnalyticsError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "unknown_error";
}

type TrackProperties = Record<
  string,
  string | number | boolean | string[] | undefined
>;

function trackEvent(
  eventName: AnalyticsEventName,
  properties: TrackProperties
): void {
  if (!shouldSendMixpanelEvents()) {
    return;
  }

  initMixpanelIfNeeded();

  const cleaned = Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined)
  );

  mixpanel.track(eventName, cleaned);
}

export { trackEvent, ANALYTICS_EVENTS };
